<script setup lang="ts">
/**
 * UiInput — текстовое поле кабинета (inbox-115).
 *
 * Types: text (default) | email | password | url
 * Когда type=password — справа eye-toggle (show/hide).
 * Error state — красная рамка + опциональный hint снизу.
 *
 * CSS портом из docs/mockups/auth-login.html (.input + .pw-wrap +
 * .pw-toggle) + lib/components.css base .input.
 *
 * Usage:
 *   <UiInput v-model="email" type="email" placeholder="you@example.ru" />
 *   <UiInput v-model="pw" type="password" :error="errors.pw" />
 */

import { computed, ref } from 'vue';

interface Props {
  modelValue?: string;
  type?: 'text' | 'email' | 'password' | 'url';
  placeholder?: string;
  autocomplete?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  name?: string;
  inputmode?: 'text' | 'email' | 'url' | 'numeric' | 'tel';
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  placeholder: '',
  autocomplete: undefined,
  disabled: false,
  required: false,
  error: '',
  hint: '',
  name: undefined,
  inputmode: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const visible = ref(false);

const effectiveType = computed(() => {
  if (props.type === 'password' && visible.value) return 'text';
  return props.type;
});

const isPassword = computed(() => props.type === 'password');

function onInput(e: Event): void {
  emit('update:modelValue', (e.target as HTMLInputElement).value);
}

function toggleVisible(): void {
  visible.value = !visible.value;
}
</script>

<template>
  <div
    class="ui-input"
    :class="{ 'has-error': !!error, disabled }"
  >
    <div
      class="wrap"
      :class="{ pw: isPassword }"
    >
      <input
        :type="effectiveType"
        :value="modelValue"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :required="required"
        :name="name"
        :inputmode="inputmode"
        class="input"
        @input="onInput"
        @blur="(e) => emit('blur', e)"
        @focus="(e) => emit('focus', e)"
      >
      <button
        v-if="isPassword"
        type="button"
        class="pw-toggle"
        :aria-label="visible ? 'Скрыть пароль' : 'Показать пароль'"
        :title="visible ? 'Скрыть' : 'Показать'"
        tabindex="-1"
        @click="toggleVisible"
      >
        <svg
          v-if="visible"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    </div>

    <p
      v-if="error"
      class="error-msg"
    >
      {{ error }}
    </p>
    <p
      v-else-if="hint"
      class="hint-msg"
    >
      {{ hint }}
    </p>
  </div>
</template>

<style scoped>
.ui-input {
  display: block;
  width: 100%;
}

.wrap {
  position: relative;
}
.wrap.pw .input {
  padding-right: 40px;
}

/* inbox-330 — единый h=40 / r=8 / fz=14 с UiSelect и UiButton.size-md. */
.input {
  width: 100%;
  min-height: 40px;
  background: var(--color-bg-elev);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-input);
  padding: 10px 14px;
  color: var(--color-fg);
  font: inherit;
  font-size: var(--fs-body);
  line-height: 1.4;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
}
.input:focus {
  box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.15);
}
.input:focus {
  outline: 0;
  border-color: var(--color-accent);
}
.input::placeholder {
  color: var(--color-fg-dim);
}
.input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.has-error .input {
  border-color: var(--color-danger);
}
.has-error .input:focus {
  border-color: var(--color-danger);
}

.pw-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--color-fg-dim);
  padding: 4px;
  display: grid;
  place-items: center;
  border-radius: 4px;
}
.pw-toggle:hover {
  color: var(--color-fg);
}
.pw-toggle svg {
  width: 16px;
  height: 16px;
}

.error-msg {
  margin-top: 4px;
  font-size: var(--fs-small);
  color: var(--color-danger);
}
.hint-msg {
  margin-top: 4px;
  font-size: var(--fs-small);
  color: var(--color-fg-dim);
}
</style>
